import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  createCompensation,
  updateCompensation,
  type Compensation,
  type CreateCompensationInput,
  type UpdateCompensationInput,
} from '@/lib/employees';
import { formatDate, parseDateOnly, toDateParam } from '@/lib/date';
import { Loader2 } from 'lucide-react';

interface CompensationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  compensation?: Compensation | null;
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'UAH', 'PLN', 'CZK', 'CHF', 'CAD', 'AUD'];

const PAYMENT_PERIODS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'semi_monthly', label: 'Semi-monthly' },
];

export function CompensationDialog({
  open,
  onOpenChange,
  employeeId,
  compensation,
}: CompensationDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = !!compensation;

  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
  const [paymentPeriod, setPaymentPeriod] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (open) {
      if (compensation) {
        setAmount(String(parseFloat(compensation.amount)));
        setCurrency(compensation.currency);
        setEffectiveDate(
          compensation.effectiveDate ? parseDateOnly(compensation.effectiveDate) : null,
        );
        setPaymentPeriod(compensation.paymentPeriod ?? '');
        setComment(compensation.comment ?? '');
      } else {
        setAmount('');
        setCurrency('USD');
        setEffectiveDate(null);
        setPaymentPeriod('');
        setComment('');
      }
    }
  }, [open, compensation]);

  const createMutation = useMutation({
    mutationFn: (input: CreateCompensationInput) =>
      createCompensation(employeeId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employee', employeeId, 'compensations'],
      });
      onOpenChange(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateCompensationInput) =>
      updateCompensation(employeeId, compensation!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employee', employeeId, 'compensations'],
      });
      onOpenChange(false);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const parsedAmount = parseFloat(amount);
  const amountValid = amount.trim() !== '' && !Number.isNaN(parsedAmount) && parsedAmount >= 0;

  // Every field the submit needs, in label order — drives both the `*` markers and the
  // "Required: …" hint on the disabled button, so a blocked submit says which field is missing.
  const missingFields: string[] = [];
  if (!amountValid) missingFields.push('Amount');
  if (!paymentPeriod) missingFields.push('Payment Period');
  if (!effectiveDate) missingFields.push('Effective Date');
  const canSubmit = missingFields.length === 0 && !isPending;

  // A future effective date is persisted as a Scheduled rate that auto-activates on that day —
  // surface that in the dialog so it isn't a surprise that only shows up post-save in the history.
  const isFutureEffective = (() => {
    if (!effectiveDate) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const eff = new Date(
      effectiveDate.getFullYear(),
      effectiveDate.getMonth(),
      effectiveDate.getDate(),
    );
    return eff.getTime() > today.getTime();
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!amountValid) return;

    const effectiveDateStr = toDateParam(effectiveDate);
    if (!effectiveDateStr) return;

    if (!paymentPeriod) return;

    if (isEditing) {
      updateMutation.mutate({
        amount: parsedAmount,
        currency,
        effectiveDate: effectiveDateStr,
        paymentPeriod,
        comment: comment || undefined,
      });
    } else {
      createMutation.mutate({
        amount: parsedAmount,
        currency,
        effectiveDate: effectiveDateStr,
        paymentPeriod,
        comment: comment || undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Compensation' : 'Add Compensation'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the compensation record details.'
              : 'Add a new compensation record for this employee.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">
                  Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="5000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={isPending}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={currency}
                  onValueChange={setCurrency}
                  disabled={isPending}
                >
                  <SelectTrigger id="currency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentPeriod">
                Payment Period <span className="text-destructive">*</span>
              </Label>
              <Select value={paymentPeriod} onValueChange={setPaymentPeriod} disabled={isPending}>
                <SelectTrigger id="paymentPeriod">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_PERIODS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="effectiveDate">
                Effective Date <span className="text-destructive">*</span>
              </Label>
              <DatePicker
                id="effectiveDate"
                value={effectiveDate}
                onChange={setEffectiveDate}
                placeholder="Select date"
                disabled={isPending}
              />
              {isFutureEffective && (
                <p className="text-xs text-muted-foreground">
                  This rate will be scheduled and become active on{' '}
                  {formatDate(toDateParam(effectiveDate), 'long')}.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment">Comment</Label>
              <Textarea
                id="comment"
                placeholder="Optional note about this compensation change..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isPending}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="sm:justify-between sm:items-center">
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {!isPending && missingFields.length > 0
                ? `Required: ${missingFields.join(', ')}`
                : ''}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Add Compensation'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
